import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

const pillars: Pillar[] = [
  {
    title: 'Ayrıcalıklı erişim, uçtan uca denetim altında',
    description:
      'Bilgi sistemleri denetimlerinin ilk baktığı yer ayrıcalıklı hesaplardır. Monopam ile bu hesaplar kasada yaşar, erişim aracıyla kurulur ve her oturum kanıtıyla birlikte kaydedilir.',
    bullets: [
      'Paylaşılan ve ayrıcalıklı hesaplar kasada, rotasyonlu',
      'Kullanıcı gerçek parolayı hiçbir aşamada görmez',
      'Video + döküm ile tam oturum kaydı',
      'Süre sınırlı erişim, onay akışlarıyla',
    ],
  },
  {
    title: 'Veritabanına giden her sorgu izli',
    description:
      'Müşteri verisinin yaşadığı yer veritabanıdır. DBA’ler kendi araçlarıyla çalışmaya devam eder; her SQL ifadesi kim, ne zaman, hangi sistemde bilgisiyle iz kaydına düşer.',
    bullets: [
      'psql, DBeaver, SSMS ile aracılı veritabanı erişimi',
      'SQL seviyesinde politika: engelle, onayla, sınırla',
      'TCKN, IBAN ve kart verisi için dinamik maskeleme',
      'Sorgu düzeyinde, kişiye bağlı iz kaydı',
    ],
  },
  {
    title: 'Görevler ayrılığı ve düzenli gözden geçirme',
    description:
      'Yetki birikmesini denetçiden önce siz görürsünüz: görevler ayrılığı kuralları talep anında uygulanır, erişimler düzenli kampanyalarla gözden geçirilir.',
    bullets: [
      'Görevler ayrılığı (SoD) kural motoru',
      'Zamanlanmış erişim gözden geçirme kampanyaları',
      'Doğrulanmayan erişimin otomatik geri alınması',
      'Yetki değişikliklerinin tam geçmişi',
    ],
  },
  {
    title: 'İz kayıtları: uzun saklama, değişmezlik',
    description:
      'Denetim izleri mevzuatın öngördüğü süreler boyunca, kurcalamaya dayanıklı biçimde saklanır ve istendiğinde rapor olarak teslim edilir.',
    bullets: [
      'Değiştirilemez, merkezi iz kaydı',
      'Mevzuat sürelerine uygun uzun saklama',
      'SIEM entegrasyonu ve imzalı webhook’lar',
      'Denetçiye hazır, zamanlanmış raporlar',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Kasaya alın',
    description:
      'Ayrıcalıklı ve paylaşılan hesapları kasaya taşıyın; rotasyonu açın, parola dolaşımını bitirin.',
  },
  {
    title: 'Politikayı kurun',
    description:
      'Erişim, onay, SoD ve veritabanı kurallarını tanımlayın: kişiye, sisteme ve saate göre.',
  },
  {
    title: 'Denetimi bekletmeyin',
    description:
      'Oturum kayıtları, sorgu izleri ve zamanlanmış raporlarla kanıt seti her an teslime hazır.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Finansal hizmetler',
    description: 'Bankalar ve ödeme kuruluşları için sektör sayfası.',
    href: '/solutions/industries/financial-services',
  },
  {
    label: 'Veritabanı erişimi',
    description: 'SQL seviyesinde politika, iz kaydı ve dinamik maskeleme.',
    href: '/pam/database-access',
  },
  {
    label: 'Yerli kimlik yönetimi',
    description: 'Veri Türkiye’de kalır; mimariyle, sözleşmeyle değil.',
    href: '/solutions/yerli-kimlik-yonetimi',
  },
]

export default function BddkErisimYonetimiPage() {
  return (
    <Layout>
      <SolutionMeta
        title="BDDK denetimine hazır kimlik ve erişim yönetimi"
        description="Bankacılık bilgi sistemleri denetimleri için erişim yönetimi: ayrıcalıklı hesaplar kasada, veritabanı sorguları kişiye bağlı iz kaydında, görevler ayrılığı talep anında, iz kayıtları mevzuat sürelerince saklı."
        path="/tr/solutions/bddk-erisim-yonetimi"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Kullanım senaryoları · Bankacılık"
          title={
            <>
              Denetçi sormadan önce
              <br />
              cevabı hazır olan banka.
            </>
          }
          body="Bilgi sistemleri denetimlerinin erişim tarafı hep aynı sorulara gelir: ayrıcalıklı hesaplar kimde, veritabanına kim ne çalıştırdı, yetkiler ne zaman gözden geçirildi, izler nerede? Monofor bu soruların cevabını denetim günü değil, her gün hazır tutar."
          primaryCta={{ label: 'Ücretsiz deneme başlat →', href: '/try/now' }}
          secondaryCta={{ label: 'Uzmanlarımızla görüşün', href: '/contact' }}
          badges={[
            'Ayrıcalıklı oturum kaydı',
            'Sorgu düzeyinde veritabanı izi',
            'SoD ve erişim gözden geçirme',
          ]}
        />
        <SolutionPillars
          eyebrow="Ne elde edersiniz"
          title={<>Denetim beklentileri, günlük işletime gömülü.</>}
          body="Amaç denetimden geçmek değil; erişim disiplinini, kanıtı yan ürün olarak üreten bir işletim biçimine dönüştürmek."
          pillars={pillars}
        />
        <ProductHowItWorks
          eyebrow="Nasıl çalışır"
          title={<>Kasaya alın. Politikayı kurun. Kanıtlayın.</>}
          steps={steps}
        />
        <SolutionRelated
          eyebrow="Keşfetmeye devam edin"
          title="Bu senaryonun arkasındaki yetenekler."
          items={related}
        />
        <AssetDownload
          asset="kvkk-rehber"
          title={{
            en: 'KVKK için kimlik ve erişim rehberi.',
            tr: 'KVKK için kimlik ve erişim rehberi.',
          }}
          copy={{
            en: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: envanterden değiştirilemez loglara. (Türkçe PDF)',
            tr: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: erişim envanterinden değiştirilemez loglara.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

// TR-only: BDDK anlatısı yalnızca Türkiye pazarına hitap eder; EN locale 404.
export async function getStaticProps({ locale }: { locale: string }) {
  if (locale !== 'tr') {
    return { notFound: true }
  }
  return { props: {} }
}
